/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import { Theme } from "@emotion/react";

export interface StyleType {
	default: ColorStyle;
	hover: ColorStyle;
	pressed: ColorStyle;
	focused: ColorStyle;
	disabled: ColorStyle;
}

interface ColorStyle {
	color: string;
	background: string;
	border?: string;
}

export interface Variant_Type {
	text: StyleType;
	contained: {
		primary: StyleType;
		secondary: StyleType;
		tertiary: StyleType;
		gradient: StyleType;
		neutral: StyleType;
	};
	outlined: StyleType;
}

export const VARIANT_PALETTE = (fill: keyof Theme["colors"] = "neutral_80_100") => {
	return {
		text: {
			default: {
				color: "primary_80_100",
				background: "transparent"
			},
			hover: {
				color: "neutral_80_100",
				background: "secondary_80_8"
			},
			pressed: {
				color: "neutral_80_100",
				background: "Secondary_80_12"
			},
			focused: {
				color: "neutral_80_100",
				background: "Secondary_80_16"
			},
			disabled: {
				color: "neutral_80_32",
				background: "primary_80_100"
			}
		},
		contained: {
			primary: {
				default: {
					color: "neutral_00_100",
					background: "primary_80_100"
				},
				hover: {
					color: "neutral_00_100",
					background: "primary_80_100_w8"
				},
				pressed: {
					color: "neutral_00_100",
					background: "primary_80_100_w12"
				},
				focused: {
					color: "neutral_00_100",
					background: "primary_80_100_w16"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_12"
				}
			},
			secondary: {
				default: {
					color: "neutral_80_100",
					background: "secondary_80_12"
				},
				hover: {
					color: "neutral_80_100",
					background: "secondary_80_16"
				},
				pressed: {
					color: "neutral_80_100",
					background: "secondary_80_20"
				},
				focused: {
					color: "neutral_80_100",
					background: "secondary_80_24"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_12"
				}
			},
			tertiary: {
				default: {
					color: "neutral_80_100",
					background: "neutral_00_100"
				},
				hover: {
					color: "neutral_80_100",
					background: "neutral_00_80"
				},
				pressed: {
					color: "neutral_80_100",
					background: "neutral_00_84"
				},
				focused: {
					color: "neutral_80_100",
					background: "neutral_00_88"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_00_68"
				}
			},
			gradient: {
				default: {
					color: "neutral_80_100",
					background: "gradient_40_100"
				},
				hover: {
					color: "neutral_80_100",
					background: "gradient_40_100_w8"
				},
				pressed: {
					color: "neutral_80_100",
					background: "gradient_40_100_w12"
				},
				focused: {
					color: "neutral_80_100",
					background: "gradient_40_100_w16"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_12"
				}
			},
			neutral: {
				default: {
					color: "neutral_80_100",
					background: "neutral_00_68"
				},
				hover: {
					color: "neutral_80_100",
					background: "neutral_00_80"
				},
				pressed: {
					color: "neutral_80_100",
					background: "neutral_00_84"
				},
				focused: {
					color: "neutral_80_100",
					background: "neutral_00_88"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_68"
				}
			},
			success: {
				default: {
					color: fill,
					background: "success_60_100"
				},
				hover: {
					color: fill,
					background: "success_50_100_w8"
				},
				pressed: {
					color: fill,
					background: "success_50_100_w12"
				},
				focused: {
					color: fill,
					background: "success_50_100_w16"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_12"
				}
			},
			error: {
				default: {
					color: fill,
					background: "error_60_100"
				},
				hover: {
					color: fill,
					background: "error_60_100_w8"
				},
				pressed: {
					color: fill,
					background: "error_60_100_w12"
				},
				focused: {
					color: fill,
					background: "error_60_100_w16"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_12"
				}
			},
			accent: {
				default: {
					color: fill,
					background: "accent_40_100"
				},
				hover: {
					color: fill,
					background: "accent_40_100"
				},
				pressed: {
					color: fill,
					background: "accent_40_100"
				},
				focused: {
					color: fill,
					background: "accent_40_100"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_12"
				}
			},
			neutral_secondary: {
				default: {
					color: "neutral_80_100",
					background: "neutral_00_68"
				},
				hover: {
					color: "neutral_80_100",
					background: "secondary_80_8"
				},
				pressed: {
					color: "neutral_80_100",
					background: "secondary_80_12"
				},
				focused: {
					color: "neutral_80_100",
					background: "secondary_80_16"
				},
				disabled: {
					color: "neutral_80_32",
					background: "neutral_80_32"
				}
			},
			transparent: {
				default: {
					color: fill,
					background: "transparent"
				},
				hover: {
					color: fill,
					background: "secondary_80_8"
				},
				pressed: {
					color: fill,
					background: "secondary_80_12"
				},
				focused: {
					color: fill,
					background: "secondary_80_16"
				},
				disabled: {
					color: "neutral_80_32",
					background: "transparent"
				}
			}
		},
		outlined: {
			default: {
				color: "neutral_00_100",
				background: "transparent",
				border: "neutral_00_32"
			},
			hover: {
				color: "neutral_00_100",
				background: "neutral_00_8",
				border: "neutral_00_32"
			},
			pressed: {
				color: "neutral_00_100",
				background: "neutral_00_12",
				border: "neutral_00_32"
			},
			focused: {
				color: "neutral_00_100",
				background: "neutral_00_16",
				border: "neutral_00_32"
			},
			disabled: {
				color: "neutral_00_32",
				background: "transparent",
				border: "neutral_00_32"
			}
		}
	};
};

export const SHAPE_PALETTE = {
	circle: "100px",
	square: "initial",
	round: "8px"
};

export const SIZE_PALETTE: DesignStuff.Button.SizeProps = {
	XXlarge: {
		style: {
			padding: "11px 20px",
			height: "44px"
		},
		fontType: "Button1"
	},
	Xlarge: {
		style: {
			padding: "10px 20px",
			height: "40px"
		},
		fontType: "Button2"
	},
	large: {
		style: {
			padding: "8px 20px",
			height: "36px"
		},
		fontType: "Button2"
	},
	medium: {
		style: {
			padding: "8px 12px",
			height: "36px"
		},
		fontType: "Button2"
	},
	small: {
		style: {
			padding: "6px 12px",
			height: "32px"
		},
		fontType: "Button2"
	},
	mini: {
		style: {
			padding: "4px 12px",
			height: "26px"
		},
		fontType: "Body5_SB"
	}
};

export const ICON_SIZE_PALETTE = {
	small: {
		style: {
			width: "24px",
			height: "24px"
		},
		iconSize: 16
	},
	small_18: {
		style: {
			width: "24px",
			height: "24px"
		},
		iconSize: 18
	},
	medium: {
		style: {
			width: "28px",
			height: "28px"
		},
		iconSize: 20
	},
	medium_18: {
		style: {
			width: "28px",
			height: "28px"
		},
		iconSize: 18
	},
	large: {
		style: {
			width: "32px",
			height: "32px"
		},
		iconSize: 24
	},
	large_20: {
		style: {
			width: "32px",
			height: "32px"
		},
		iconSize: 20
	},
	large_50: {
		style: {
			width: "50px",
			height: "50px"
		},
		iconSize: 24
	},
	Xlarge: {
		style: {
			width: "36px",
			height: "36px"
		},
		iconSize: 24
	},
	XXlarge: {
		style: {
			width: "40px",
			height: "40px"
		},
		iconSize: 24
	},
	XXXlarge: {
		style: {
			width: "64px",
			height: "64px"
		},
		iconSize: 40
	}
};

export const TOOLBAR_SIZE = {
	large: {
		width: "60px",
		height: "60px"
	}
};
